import type { Op } from '@scribbleplot/shared';

/**
 * Collapse runs of field updates into one op.
 *
 * A drag emits a `set` per pointer frame. Keeping all of them would make undo
 * step through the drag one frame at a time and bloat every history entry, so
 * consecutive `set`s on the same collection merge into a single op with
 * last-write-wins per field.
 *
 * Only *consecutive* sets merge: an `add` or `del` in between is a barrier,
 * because reordering across it could change the result.
 */
export function coalesceOps(ops: readonly Op[]): Op[] {
  const out: Op[] = [];

  for (const op of ops) {
    const last = out[out.length - 1];
    if (op.t === 'set' && last?.t === 'set' && last.c === op.c) {
      const merged = new Map<string, Record<string, unknown>>();
      for (const { id, patch } of last.p) merged.set(id, { ...patch });
      for (const { id, patch } of op.p) merged.set(id, { ...(merged.get(id) ?? {}), ...patch });
      out[out.length - 1] = { t: 'set', c: op.c, p: [...merged].map(([id, patch]) => ({ id, patch })) };
      continue;
    }
    out.push(op);
  }

  return out;
}
